/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';
import {getPayload, type Payload} from 'payload';

import {Button} from 'UI/components/action/Button';
import {LanguageSwitch} from 'UI/components/action/LanguageSwitch';
import {Headline} from 'UI/components/layout/Text';
import {ChangePasswordSection} from 'UI/components/modules/ChangePasswordSection';
import {GroupListeningSection} from 'UI/components/modules/GroupListeningSection';
import {UserNameEditor} from 'UI/components/modules/UserNameEditor';

import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import payloadConfig from 'Payload/payload.config';
import {useSettingsPage} from 'UI/hooks/useSettingsPage';
import {AppLayout} from 'UI/layouts/AppLayout';

import {SettingsIcon} from 'Images/icons';
import {getLegalPages} from 'Payload/utils/getLegalPages';

import type {GetStaticProps, InferGetStaticPropsType} from 'next';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Config, LegalPage} from 'types/payload-types';

interface SettingsPageProps {
    legalPages: LegalPage[];
}

/**
 * Einstellungsseite. Zeigt Sprachauswahl für alle Nutzer
 * und zusätzlich Konto-Infos mit Logout für angemeldete Nutzer.
 *
 * @param props            Props.
 * @param props.legalPages Legalpages.
 * @returns ReactNode der Einstellungsseite.
 */
const SettingsPage: NextSSGPageWithLayout<typeof getStaticProps> = (
    {legalPages}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {
        analyticsUpdating,
        handleAnalyticsLinkingToggle,
        handleLogout,
        isAdmin,
        isLoading,
        locationEnabled,
        loggingOut,
        permissionState,
        t,
        toggleLocation,
        user
    } = useSettingsPage();

    return (
        <Content>
            <Header>
                <SettingsIcon height={28} width={28} />
                <Headline $size="headline" as="h1">{t('settings.title')}</Headline>
            </Header>
            <Section>
                <LanguageSwitch />
            </Section>
            <Section>
                <SectionLabel>{t('settings.location')}</SectionLabel>
                <LocationCard data-cy="SettingsPage-location">
                    <LocationRow>
                        <LocationText>
                            <LocationTitle>
                                {locationEnabled ? t('settings.locationEnabled') : t('settings.locationDisabled')}
                            </LocationTitle>
                            <LocationDescription>{t('settings.locationDescription')}</LocationDescription>
                        </LocationText>
                        <ToggleTrack
                            $active={locationEnabled}
                            aria-checked={locationEnabled}
                            data-cy="SettingsPage-location-toggle"
                            role="switch"
                            type="button"
                            onClick={toggleLocation}
                        >
                            <ToggleThumb $active={locationEnabled} />
                        </ToggleTrack>
                    </LocationRow>
                    {permissionState === 'denied' && (
                        <DeniedHint data-cy="SettingsPage-location-denied">
                            {t('settings.locationDenied')}
                        </DeniedHint>
                    )}
                </LocationCard>
            </Section>
            {!isLoading && user && (
                <Section>
                    <SectionLabel>{t('settings.account')}</SectionLabel>
                    <AccountCard data-cy="SettingsPage-account">
                        <UserNameEditor />
                        <UserEmail>{user.email}</UserEmail>
                        <PurchasesLink data-cy="SettingsPage-purchases" href="/account/purchases">
                            {t('purchases.title')}
                        </PurchasesLink>
                        <Button
                            data-cy="SettingsPage-logout"
                            disabled={loggingOut}
                            size="small"
                            type="button"
                            variant="primaryGradient"
                            onClick={() => {
                                void handleLogout();
                            }}
                        >
                            {loggingOut ? t('account.loggingOut') : t('account.logout')}
                        </Button>
                    </AccountCard>
                </Section>
            )}
            {!isLoading && user && (
                <Section>
                    <SectionLabel>{t('settings.privacy')}</SectionLabel>
                    <LocationCard data-cy="SettingsPage-analytics">
                        <LocationRow>
                            <LocationText>
                                <LocationTitle>
                                    {user.analyticsOptOut
                                        ? t('settings.analyticsLinkingDisabled')
                                        : t('settings.analyticsLinkingEnabled')}
                                </LocationTitle>
                                <LocationDescription>{t('settings.analyticsLinkingDescription')}</LocationDescription>
                                <LocationDescription data-cy="SettingsPage-analytics-logoutHint">
                                    {t('settings.analyticsLinkingLogoutHint')}
                                </LocationDescription>
                            </LocationText>
                            <ToggleTrack
                                $active={!user.analyticsOptOut}
                                aria-checked={!user.analyticsOptOut}
                                data-cy="SettingsPage-analytics-toggle"
                                disabled={analyticsUpdating}
                                role="switch"
                                type="button"
                                onClick={() => {
                                    void handleAnalyticsLinkingToggle(Boolean(user.analyticsOptOut));
                                }}
                            >
                                <ToggleThumb $active={!user.analyticsOptOut} />
                            </ToggleTrack>
                        </LocationRow>
                    </LocationCard>
                </Section>
            )}
            {!isLoading && user && (
                <Section>
                    <SectionLabel>{t('changePassword.title')}</SectionLabel>
                    <AccountCard data-cy="SettingsPage-account">
                        <ChangePasswordSection />
                    </AccountCard>
                </Section>
            )}
            {!isLoading && user && user.groupListeningEnabled && (
                <Section>
                    <SectionLabel>{t('group.title')}</SectionLabel>
                    <AccountCard data-cy="SettingsPage-group">
                        <GroupListeningSection />
                    </AccountCard>
                </Section>
            )}
            {!isLoading && !user && isAdmin && (
                <Section>
                    <SectionLabel>{t('settings.account')}</SectionLabel>
                    <AccountCard data-cy="SettingsPage-admin-account">
                        <UserName>Admin</UserName>
                        <Button
                            data-cy="SettingsPage-logout"
                            disabled={loggingOut}
                            size="small"
                            type="button"
                            variant="primaryGradient"
                            onClick={() => {
                                void handleLogout();
                            }}
                        >
                            {loggingOut ? t('account.loggingOut') : t('account.logout')}
                        </Button>
                    </AccountCard>
                </Section>
            )}
            {!isLoading && !user && !isAdmin && (
                <Section>
                    <LoginLink href="/login">{t('nav.login')}</LoginLink>
                </Section>
            )}
            <Section>
                <SectionLabel>{t('settings.legal')}</SectionLabel>
                <LegalList data-cy="SettingsPage-legal">
                    {Array.from(legalPages, page => (
                        <LegalLink key={page.id} href={`/legal/${page.slug}`}>{page.title}</LegalLink>
                    ))}
                </LegalList>
            </Section>
        </Content>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout (Header + Navigation).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
SettingsPage.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="settings" legalPages={pageProps.legalPages}>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
SettingsPage.getLayoutKey = () => 'settings';

export default SettingsPage;

// ── Styled ──────────────────────────────────────────────────

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(10)};
    padding: ${spacing(6)};
`;

const Header = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(3)};
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const SectionLabel = styled.span`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;

/** Props for toggle styled components. */
interface ToggleProps {
    /** Whether the toggle is in the active/on state. */
    $active: boolean;
}

const LocationCard = styled.div`
    border: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    padding: ${spacing(6)};
`;

const LocationRow = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(4)};
    justify-content: space-between;
`;

const LocationText = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${spacing(1)};
`;

const LocationTitle = styled.span`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 600;
`;

const LocationDescription = styled.span`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    line-height: 1.4;
`;

const ToggleTrack = styled.button<ToggleProps>`
    background: ${({$active, theme}) => ($active ? theme.colors.primaryDefault : theme.colors.surfaceDim)};
    border: none;
    border-radius: 14px;
    cursor: pointer;
    flex-shrink: 0;
    height: 28px;
    padding: 2px;
    position: relative;
    transition: background 0.2s ease;
    width: 48px;
`;

const ToggleThumb = styled.span<ToggleProps>`
    background: ${({theme}) => theme.colors.white};
    border-radius: 50%;
    display: block;
    height: 24px;
    transform: translateX(${({$active}) => ($active ? '20px' : '0')});
    transition: transform 0.2s ease;
    width: 24px;
`;

const DeniedHint = styled.span`
    color: ${({theme}) => theme.colors.error};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    line-height: 1.4;
`;

const AccountCard = styled.div`
    align-items: flex-start;
    border: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    max-width: 100%;
    padding: ${spacing(6)};
    width: 100%;
`;

const UserEmail = styled.span`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    max-width: 100%;
    overflow-wrap: break-word;
`;

const PurchasesLink = styled(Link)`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 600;
    margin-top: ${spacing(2)};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const UserName = styled.span`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2rem;
    font-weight: 700;
    max-width: 100%;
    overflow-wrap: break-word;
`;

const LoginLink = styled(Link)`
    background: ${({theme}) => theme.colors.primaryDefault};
    border-radius: 8px;
    color: ${({theme}) => theme.colors.white};
    display: block;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 600;
    padding: ${spacing(4)};
    text-align: center;
    text-decoration: none;
`;

const LegalList = styled.div`
    border: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const LegalLink = styled(Link)`
    border-bottom: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    padding: ${spacing(4)} ${spacing(6)};
    text-decoration: none;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${({theme}) => theme.colors.surfaceHigh};
    }
`;

/**
 * Lädt Settingspage.
 *
 * @param props                  Die Static-Props Kontext-Optionen.
 * @param props.locale           Die aktuelle Locale.
 * @param props.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit Homepage-Config, Walks, Categories und Cities.
 */
export const getStaticProps: GetStaticProps<SettingsPageProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {legalPages: []},
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];
    const legalPages = await getLegalPages(payload, payloadLocale);

    return {
        props: {legalPages},
        revalidate: 18000
    };
};